import { Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@app/../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap, BehaviorSubject, catchError, of } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

interface VerifyTokenResponse {
  success: boolean;
  message: string;
  token: string;
}

interface DecodedToken {
  sub: string;
  name: string;
  iat: number;
  exp: number;
  apikey: string;
  [key: string]: unknown;
  organization: string;
}

/**
 * JWT Service - Handles authentication token management
 *
 * SECURITY NOTES:
 * - Tokens are stored in sessionStorage (cleared on tab close)
 * - Tokens from URL are immediately cleared after extraction
 * - Sensitive operations are not logged in production
 */
@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private readonly VERIFY_TOKEN_URL = `${environment.apiUrl}/auth/login`;
  private readonly LOGOUT_URL = `${environment.apiUrl}/auth/logout`;

  // BehaviorSubject pour observer les changements du token
  private tokenSubject = new BehaviorSubject<DecodedToken | null>(null);

  // Observable public pour que les composants puissent s'abonner
  public token$ = this.tokenSubject.asObservable();

  // Flag pour éviter les vérifications multiples
  private hasCheckedUrl = false;

  constructor(private http: HttpClient, private router: Router) {
    // Only log initialization details in development mode
    if (isDevMode()) {
      console.log('[JwtService] Initialized in development mode');
    }

    // Initialiser le token au démarrage
    this.initializeToken();
  }

  /**
   * Charge le token depuis la configuration de développement
   * Utilisé uniquement en mode développement
   */
  private loadDevToken(): string | null {
    if (!environment.production) {
      // Récupérer le token depuis le fichier environment
      const devToken = (environment as Record<string, unknown>)['devJwtToken'] as string | undefined;
      if (devToken && devToken.length > 0 && devToken !== 'your-jwt-token-here') {
        if (isDevMode()) {
          console.log('[JwtService] Dev token loaded from configuration');
        }
        return devToken;
      }
    }
    return null;
  }

  /**
   * Initialise le token au démarrage du service
   * Vérifie d'abord l'URL (hash puis query), puis le sessionStorage, puis le token de dev
   */
  private initializeToken(): void {
    // Vérifier d'abord s'il y a un token dans l'URL
    const tokenFromUrl = this.getTokenFromUrl();
    if (tokenFromUrl) {
      this.setToken(tokenFromUrl);
      // Nettoyer l'URL immédiatement après extraction (sécurité)
      this.clearTokenFromUrl();
      // Marquer comme vérifié pour éviter les vérifications répétitives
      this.hasCheckedUrl = true;
      return;
    }

    // Sinon, vérifier le sessionStorage
    const decodedToken = this.decodeToken();
    if (decodedToken) {
      this.tokenSubject.next(decodedToken);
      return;
    }

    // En mode développement, charger le token depuis la configuration
    const devToken = this.loadDevToken();
    if (devToken) {
      this.setToken(devToken);
      return;
    }

    // Aucun token trouvé
    this.tokenSubject.next(null);
  }

  /**
   * Vérifie si le token JWT est valide en appelant l'API externe
   * @returns Observable<boolean>
   */
  public verifyTokenWithApi(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(false);
    }

    return this.http.post<VerifyTokenResponse>(this.VERIFY_TOKEN_URL, { token },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    )
      .pipe(
        map(response => response.success === true),
        catchError((error) => {
          // Log errors only in development mode
          if (isDevMode()) {
            console.error('[JwtService] Token verification failed:', error.status);
          }
          return of(false);
        })
      );
  }

  /**
   * Récupère le token depuis le sessionStorage
   * @returns string | null
   */
  public getToken(): string | null {
    return sessionStorage.getItem('startupkit_SESSION');
  }

  /**
   * Sauvegarde le token dans le sessionStorage
   * @param token string
   */
  public setToken(token: string): void {
    // Validate token format before storing (basic JWT structure check)
    if (!this.isValidJwtFormat(token)) {
      if (isDevMode()) {
        console.warn('[JwtService] Invalid JWT format, token not stored');
      }
      return;
    }

    sessionStorage.setItem('startupkit_SESSION', token);
    // Émettre le nouveau token décodé
    const decodedToken = this.decodeToken();
    this.tokenSubject.next(decodedToken);
  }

  /**
   * Validates basic JWT format (three base64 parts separated by dots)
   */
  private isValidJwtFormat(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    // Check each part is valid base64url
    const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
    return parts.every(part => part.length > 0 && base64UrlRegex.test(part));
  }

  /**
   * Supprime le token du sessionStorage
   * @param redirectToLogin - Si true, redirige automatiquement vers la page de login
   */
  public removeToken(redirectToLogin: boolean = false): void {
    // Supprimer le token local immédiatement
    this.clearLocalToken();

    // Appeler l'API de logout en arrière-plan (optionnel)
    this.removeTokenUser().subscribe({
      error: (error) => {
        // Silent fail for logout - token is already cleared locally
        if (isDevMode()) {
          console.warn('[JwtService] Logout API call failed (non-critical)');
        }
      }
    });

    // Rediriger si demandé
    if (redirectToLogin) {
      this.redirectLoginPage();
    }
  }

  private clearLocalToken(): void {
    sessionStorage.removeItem('startupkit_SESSION');
    // Émettre null pour indiquer qu'il n'y a plus de token
    this.tokenSubject.next(null);
  }

  private removeTokenUser(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(false);
    }

    return this.http.post<{ success?: boolean }>(this.LOGOUT_URL, { token }, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).pipe(
      map((res) => res?.success === true),
      catchError(() => of(false))
    );
  }

  /**
   * Vérifie le token avec l'API et redirige si nécessaire
   * @returns Observable<boolean>
   */
  public checkTokenAndRedirect(): Observable<boolean> {
    // Vérifier l'URL seulement si on n'a pas encore vérifié et qu'on n'a pas de token en session
    const existingToken = this.getToken();

    if (!existingToken && !this.hasCheckedUrl) {
      const tokenFromUrl = this.getTokenFromUrl();
      if (tokenFromUrl) {
        this.setToken(tokenFromUrl);
        // Nettoyer l'URL après avoir récupéré le token
        this.clearTokenFromUrl();
      }
      this.hasCheckedUrl = true;
    }

    return this.verifyTokenWithApi().pipe(
      map(isValid => {
        if (!isValid) {
          this.removeToken();
          // Réinitialiser le flag si le token est invalide
          this.hasCheckedUrl = false;
          return false;
        }
        return true;
      }),
      catchError(() => {
        this.removeToken();
        this.hasCheckedUrl = false;
        return of(false);
      })
    );
  }

  public redirectLoginPage(): void {
    this.router.navigate(['/unauthorized']);
  }

  /**
   * Récupère le token depuis l'URL
   * SECURITY: Checks hash fragment first (not logged by servers),
   * then falls back to query parameter for backwards compatibility.
   * Token is cleared from URL immediately after extraction.
   *
   * @returns string | null
   */
  public getTokenFromUrl(): string | null {
    // Priority 1: Check hash fragment (more secure - not sent to servers)
    // Format: #token=xxx or #access_token=xxx
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const tokenFromHash = hashParams.get('token') || hashParams.get('access_token');
      if (tokenFromHash) {
        return tokenFromHash;
      }
    }

    // Priority 2: Check query parameter (backwards compatibility)
    // WARNING: Query parameters are logged in browser history and server logs
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromQuery = urlParams.get('token');
    if (tokenFromQuery) {
      if (isDevMode()) {
        console.warn('[JwtService] Token retrieved from query parameter. Consider using hash fragment for better security.');
      }
      return tokenFromQuery;
    }

    return null;
  }

  /**
   * Nettoie le token de l'URL après l'avoir récupéré
   * Removes token from both hash fragment and query parameters
   */
  private clearTokenFromUrl(): void {
    const url = new URL(window.location.href);
    let urlChanged = false;

    // Clear from query parameters
    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      urlChanged = true;
    }

    // Clear from hash fragment
    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.substring(1));
      if (hashParams.has('token') || hashParams.has('access_token')) {
        hashParams.delete('token');
        hashParams.delete('access_token');
        const newHash = hashParams.toString();
        url.hash = newHash ? '#' + newHash : '';
        urlChanged = true;
      }
    }

    // Update URL without triggering navigation
    if (urlChanged) {
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  /**
   * Décode le token JWT
   * @returns DecodedToken | null
   */
  public decodeToken(): DecodedToken | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const decoded = jwtDecode<DecodedToken>(token);

      // Validate required fields
      if (!decoded.sub || !decoded.exp) {
        if (isDevMode()) {
          console.warn('[JwtService] Token missing required fields');
        }
        return null;
      }

      return decoded;
    } catch (error) {
      if (isDevMode()) {
        console.error('[JwtService] Error decoding token');
      }
      return null;
    }
  }

  /**
   * Check if the current token is expired
   * @returns boolean - true if expired or no token
   */
  public isTokenExpired(): boolean {
    const decoded = this.decodeToken();
    if (!decoded) {
      return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  }

  /**
   * Met à jour l'observable avec le token actuel
   * Utile pour forcer une mise à jour
   */
  public updateTokenObservable(): void {
    const decodedToken = this.decodeToken();
    this.tokenSubject.next(decodedToken);
  }
}
